import { useAuth } from '@/core/contexts/AuthContext';
import { trackEmiratesIdLoginStartClick } from '@/core/services/trackDynatraceCatalog';
import { resolveWelcomeLoginIntent } from '@/modules/welcome/utils/welcomeLoginQuery';
import {
  ensureWelcomeLoginDialogKeep,
  getWelcomeOpenLoginToken,
  isWelcomeLoginQueryDismissed,
  markWelcomeOpenLogin,
  setWelcomeLoginDialogStep,
  shouldKeepWelcomeLoginDialogOpen,
} from '@/modules/welcome/utils/welcomeOpenLoginFlag';
import { useFocusEffect } from '@react-navigation/native';
import { useGlobalSearchParams } from 'expo-router';
import { useCallback, useEffect } from 'react';

/**
 * Opens Welcome login dialog from CTA / `?login=1` / `?method=emirates-id`.
 * Does nothing while the dialog is open or after the user has closed it.
 */
export function useWelcomeWebOpenLoginFromQuery(
  openLoginDialog: () => void,
  isDialogVisible: boolean,
): void {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const globalParams = useGlobalSearchParams<{
    login?: string | string[];
    openLogin?: string | string[];
    method?: string | string[];
  }>();

  const tryOpenLoginDialog = useCallback(() => {
    if (authLoading || isAuthenticated || isDialogVisible) {
      return;
    }
    if (isWelcomeLoginQueryDismissed()) {
      return;
    }

    const { fromQuery, fromEmiratesId } = resolveWelcomeLoginIntent({
      login: globalParams.login,
      openLogin: globalParams.openLogin,
      method: globalParams.method,
    });

    if (fromQuery) {
      if (getWelcomeOpenLoginToken() === 0) {
        markWelcomeOpenLogin(fromEmiratesId ? 'EMIRATES_ID' : 'LOGIN');
      } else if (fromEmiratesId) {
        setWelcomeLoginDialogStep('EMIRATES_ID');
      }
      if (fromEmiratesId) {
        trackEmiratesIdLoginStartClick();
      }
      ensureWelcomeLoginDialogKeep();
      openLoginDialog();
      return;
    }

    const token = getWelcomeOpenLoginToken();
    const keepOpen = shouldKeepWelcomeLoginDialogOpen();
    if (!keepOpen && token === 0) {
      return;
    }

    ensureWelcomeLoginDialogKeep();
    openLoginDialog();
  }, [
    authLoading,
    isAuthenticated,
    globalParams.login,
    globalParams.openLogin,
    globalParams.method,
    isDialogVisible,
    openLoginDialog,
  ]);

  useEffect(() => {
    tryOpenLoginDialog();
  }, [tryOpenLoginDialog]);

  useFocusEffect(
    useCallback(() => {
      tryOpenLoginDialog();
    }, [tryOpenLoginDialog]),
  );
}
